import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { paths } from '@/config/paths'
import { useAccount } from '@/domain/hooks/useAccount'
import { getDisplayToken } from '@/domain/hooks/useDisplayToken'
import { useSideDrawer } from '@/domain/hooks/useSideDrawer'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { Objective } from '@/features/actions/logic/types'
import { DialogActionsPanel } from '@/features/dialogs/common/components/DialogActionsPanel'
import { assets } from '@/ui/assets'
import { Button, LinkButton } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { ArrowUpDownIcon } from 'lucide-react'
import Image from 'next/image'
import { erc20Abi, formatUnits, zeroAddress } from 'viem'
import { useReadContracts } from 'wagmi'
import { Borrow, Deposit } from '../../logic/useMyPortfolio'

type MigrationStep = 'repay' | 'withdraw' | 'openEuler'

const migrationSteps: Array<{
  id: MigrationStep
  number: number
  title: string
  description: string
}> = [
  {
    id: 'repay',
    number: 1,
    title: 'Repay debts',
    description: 'Close isolated borrow balances before moving collateral.',
  },
  {
    id: 'withdraw',
    number: 2,
    title: 'Withdraw from isolated',
    description: 'Return supplied assets and collateral to your wallet.',
  },
  {
    id: 'openEuler',
    number: 3,
    title: 'Open new position',
    description: 'Create a new position in active markets.',
  },
]

export interface IsolatedMigrationWorkspaceProps {
  deposits: Deposit[]
  borrows: Borrow[]
}

function getInitialStep(deposits: Deposit[], borrows: Borrow[]): MigrationStep {
  if (borrows.length > 0) return 'repay'
  if (deposits.length > 0) return 'withdraw'
  return 'openEuler'
}

function isStepDisabled(
  step: MigrationStep,
  deposits: Deposit[],
  borrows: Borrow[],
  completedSteps: Partial<Record<MigrationStep, boolean>>,
) {
  if (completedSteps[step] === true) return true
  if (step === 'repay') return borrows.length === 0
  if (step === 'withdraw') return deposits.length === 0
  return false
}

export function IsolatedMigrationWorkspace({ deposits, borrows }: IsolatedMigrationWorkspaceProps) {
  const steps = migrationSteps
  const [activeStep, setActiveStep] = useState<MigrationStep>(() => getInitialStep(deposits, borrows))
  const [completedSteps, setCompletedSteps] = useState<Partial<Record<MigrationStep, boolean>>>({})

  useEffect(() => {
    if (activeStep === 'repay' && borrows.length === 0) {
      setActiveStep(deposits.length > 0 ? 'withdraw' : 'openEuler')
      return
    }

    if (activeStep === 'withdraw' && deposits.length === 0) {
      setActiveStep(borrows.length > 0 ? 'repay' : 'openEuler')
    }
  }, [activeStep, borrows.length, deposits.length])

  function completeStep(step: MigrationStep) {
    setCompletedSteps((completedSteps) => ({ ...completedSteps, [step]: true }))

    if (step === 'repay') {
      setActiveStep(deposits.length > 0 ? 'withdraw' : 'openEuler')
    }
    if (step === 'withdraw') {
      setActiveStep('openEuler')
    }
  }

  function resetStep(step: MigrationStep) {
    setCompletedSteps((completedSteps) => ({ ...completedSteps, [step]: false }))
  }

  return (
    <div className="mb-6 grid gap-4 xl:grid-cols-[1fr_1.5fr]">
      <Panel className="bg-panel">
        <Panel.Header>
          <Panel.Title className="text-xl" gradient>
            Migration helper
          </Panel.Title>
        </Panel.Header>
        <Panel.Content className="grid gap-3">
          {steps.map((step) => (
            <StepButton
              key={step.id}
              step={step}
              active={activeStep === step.id}
              disabled={isStepDisabled(step.id, deposits, borrows, completedSteps)}
              count={getStepCount(step.id, deposits, borrows)}
              onClick={() => setActiveStep(step.id)}
            />
          ))}
        </Panel.Content>
      </Panel>

      <Panel className="bg-panel">
        <Panel.Header>
          <Panel.Title className="text-xl">{steps.find((step) => step.id === activeStep)?.title}</Panel.Title>
        </Panel.Header>
        <Panel.Content>
          {activeStep === 'repay' && (
            <RepayStep
              borrows={borrows}
              completed={completedSteps.repay === true}
              onComplete={() => completeStep('repay')}
              onReset={() => resetStep('repay')}
            />
          )}
          {activeStep === 'withdraw' && (
            <WithdrawStep
              deposits={deposits}
              completed={completedSteps.withdraw === true}
              onComplete={() => completeStep('withdraw')}
              onReset={() => resetStep('withdraw')}
            />
          )}
          {activeStep === 'openEuler' && <OpenNewPositionStep />}
        </Panel.Content>
      </Panel>
    </div>
  )
}

function StepButton({
  step,
  active,
  disabled,
  count,
  onClick,
}: {
  step: (typeof migrationSteps)[number]
  active: boolean
  disabled: boolean
  count: number
  onClick: () => void
}) {
  return (
    <button
      disabled={disabled}
      className={cn(
        'grid grid-cols-[auto_1fr] items-start gap-3 rounded-md border p-3 text-left transition-colors disabled:cursor-default disabled:opacity-70',
        active ? 'border-primary/50 bg-panel' : 'border-border bg-panel hover:bg-white/[.06]',
      )}
      onClick={onClick}
    >
      <span
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-full border font-semibold text-xs',
          active ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground',
        )}
      >
        {step.number}
      </span>
      <div className="flex min-w-0 flex-col">
        <span className="flex items-start justify-between gap-3">
          <Typography className="font-semibold text-sm">{step.title}</Typography>
          <span className="rounded-sm bg-white/[.06] px-2 py-1 text-muted-foreground text-xs">{count}</span>
        </span>
        <Typography variant="prompt" className="mt-1 text-sm leading-relaxed">
          {step.description}
        </Typography>
      </div>
    </button>
  )
}

function RepayStep({
  borrows,
  completed,
  onComplete,
  onReset,
}: {
  borrows: Borrow[]
  completed: boolean
  onComplete: () => void
  onReset: () => void
}) {
  const [started, setStarted] = useState(false)
  const [startedObjectives, setStartedObjectives] = useState<Objective[] | undefined>()
  const [startedBorrows, setStartedBorrows] = useState<Borrow[] | undefined>()
  const startedFundingIssues = useRef<RepayFundingIssue[]>([])
  const [repayActionsInProgress, setRepayActionsInProgress] = useState(false)
  const [pausedRepaySnapshot, setPausedRepaySnapshot] = useState<
    { runnableObjectives: string; fundingIssues: string } | undefined
  >()
  const finishedRepayBatchSignature = useRef<string | undefined>(undefined)
  const { swapDialog } = useSideDrawer()
  const objectives = useMemo<Objective[]>(() => borrows.map(createRepayObjective), [borrows])
  const {
    runnableObjectives,
    fundingIssues,
    isLoading: isFundingLoading,
  } = useRepayFundingPlan(startedBorrows ?? borrows, started)
  const runnableObjectivesSignature = useMemo(() => getObjectivesSignature(runnableObjectives), [runnableObjectives])
  const fundingIssuesSignature = useMemo(() => getFundingIssuesSignature(fundingIssues), [fundingIssues])

  useEffect(() => {
    if (
      !pausedRepaySnapshot ||
      isFundingLoading ||
      (pausedRepaySnapshot.runnableObjectives === runnableObjectivesSignature &&
        pausedRepaySnapshot.fundingIssues === fundingIssuesSignature)
    ) {
      return
    }

    setPausedRepaySnapshot(undefined)
  }, [fundingIssuesSignature, isFundingLoading, pausedRepaySnapshot, runnableObjectivesSignature])

  useLayoutEffect(() => {
    if (!started || startedObjectives || pausedRepaySnapshot || repayActionsInProgress || isFundingLoading) {
      return
    }

    if (runnableObjectives.length === 0) {
      startedFundingIssues.current = fundingIssues
      return
    }

    finishedRepayBatchSignature.current = undefined
    setStartedObjectives(runnableObjectives)
    startedFundingIssues.current = fundingIssues
  }, [
    fundingIssues,
    isFundingLoading,
    pausedRepaySnapshot,
    repayActionsInProgress,
    runnableObjectives,
    started,
    startedObjectives,
  ])

  const actionObjectives = startedObjectives ?? (started ? runnableObjectives : objectives)
  const canRunRepayActions = !isFundingLoading && actionObjectives.length > 0

  if (!started && actionObjectives.length === 0) {
    return <EmptyStep title="No isolated debt" description="You can move on to withdrawing isolated deposits." />
  }

  if (completed) {
    return (
      <CompletedStep
        title="All repay transactions completed"
        description="Refresh balances if positions are still visible while indexer data catches up."
        onReset={() => {
          setStarted(false)
          setStartedObjectives(undefined)
          setStartedBorrows(undefined)
          onReset()
        }}
      />
    )
  }

  return (
    <div className="grid gap-4">
      <StepSummary
        title="Repay all isolated debt"
        description="Max repay is selected for every isolated borrow. You will confirm each repay transaction in your wallet."
        primaryLabel={started ? 'Repay flow started' : 'Start repay all'}
        disabled={started}
        onStart={() => {
          setStartedBorrows(borrows)
          setStarted(true)
        }}
      />
      {started && (
        <>
          {actionObjectives.length > 0 && (
            <div className="no-scrollbar max-h-[420px] overflow-y-auto">
              <DialogActionsPanel
                objectives={actionObjectives}
                enabled={canRunRepayActions}
                setActionsInProgress={setRepayActionsInProgress}
                onFinish={() => {
                  const finishedSignature = getObjectivesSignature(actionObjectives)
                  if (finishedRepayBatchSignature.current === finishedSignature) {
                    return
                  }

                  finishedRepayBatchSignature.current = finishedSignature

                  if (startedFundingIssues.current.length === 0) {
                    onComplete()
                  } else {
                    setStartedBorrows(startedFundingIssues.current.map((issue) => issue.borrow))
                    setPausedRepaySnapshot({
                      runnableObjectives: finishedSignature,
                      fundingIssues: getFundingIssuesSignature(startedFundingIssues.current),
                    })
                    setStartedObjectives(undefined)
                  }
                }}
              />
            </div>
          )}
          <SwapHelpPanel
            fundingIssues={fundingIssues}
            isCheckingBalances={isFundingLoading}
            startIndex={actionObjectives.length * 2 + 1}
            onOpenSwap={swapDialog.open}
          />
        </>
      )}
    </div>
  )
}

function createRepayObjective(borrow: Borrow): Objective {
  return {
    type: 'isolatedRepay',
    token: getDisplayToken(borrow.chainId, borrow.borrowToken),
    value: borrow.borrowAmount,
    pairAddress: borrow.pairAddress,
    shares: '0',
    approvalValue: NormalizedUnitNumber(borrow.borrowAmount.multipliedBy(1.001)),
    repayFull: true,
  }
}

function getObjectivesSignature(objectives: Objective[]): string {
  return JSON.stringify(
    objectives.map((objective) => ({
      type: objective.type,
      token:
        'token' in objective
          ? objective.token.address
          : 'reserve' in objective
            ? objective.reserve.token.address
            : undefined,
      value: 'value' in objective && objective.value !== undefined ? objective.value.toFixed() : undefined,
      pairAddress: 'pairAddress' in objective ? objective.pairAddress : undefined,
      shares: 'shares' in objective ? objective.shares : undefined,
    })),
  )
}

interface RepayFundingIssue {
  borrow: Borrow
  token: Token
  value: NormalizedUnitNumber
  required: NormalizedUnitNumber
  balance?: NormalizedUnitNumber
}

function getFundingIssuesSignature(fundingIssues: RepayFundingIssue[]): string {
  return JSON.stringify(
    fundingIssues.map((issue) => ({
      token: issue.token.address,
      value: issue.value.toFixed(),
      required: issue.required.toFixed(),
      balance: issue.balance?.toFixed(),
    })),
  )
}

function useRepayFundingPlan(
  borrows: Borrow[],
  enabled: boolean,
): { runnableObjectives: Objective[]; fundingIssues: RepayFundingIssue[]; isLoading: boolean } {
  const account = useAccount()
  const requirements = useMemo(() => {
    const requirements = new Map<string, { token: Token; chainId: number; required: NormalizedUnitNumber }>()

    for (const borrow of borrows) {
      const existing = requirements.get(borrow.borrowToken.address)
      requirements.set(borrow.borrowToken.address, {
        token: borrow.borrowToken,
        chainId: borrow.chainId,
        required: NormalizedUnitNumber((existing?.required ?? NormalizedUnitNumber(0)).plus(borrow.borrowAmount)),
      })
    }

    return Array.from(requirements.values())
  }, [borrows])

  const { data, isLoading, isFetching } = useReadContracts({
    contracts: requirements.map((requirement) => ({
      address: requirement.token.address,
      abi: erc20Abi,
      functionName: 'balanceOf',
      args: [account ?? zeroAddress],
      chainId: requirement.chainId,
    })),
    query: {
      enabled: enabled && Boolean(account) && requirements.length > 0,
    },
  })

  const balancesByTokenAddress = useMemo(() => {
    const balances = new Map<string, NormalizedUnitNumber>()

    requirements.forEach((requirement, index) => {
      const result = data?.[index]?.result

      if (typeof result !== 'bigint') {
        return
      }

      balances.set(requirement.token.address, NormalizedUnitNumber(formatUnits(result, requirement.token.decimals)))
    })

    return balances
  }, [data, requirements])

  const { runnableObjectives, fundingIssues } = useMemo(() => {
    const spentByTokenAddress = new Map<string, NormalizedUnitNumber>()
    const runnableObjectives: Objective[] = []
    const fundingIssues: RepayFundingIssue[] = []

    for (const borrow of borrows) {
      const balance = balancesByTokenAddress.get(borrow.borrowToken.address)
      const spent = spentByTokenAddress.get(borrow.borrowToken.address) ?? NormalizedUnitNumber(0)
      const requiredWithBuffer = NormalizedUnitNumber(spent.plus(borrow.borrowAmount.multipliedBy(1.001)))

      if (!balance) {
        fundingIssues.push({
          borrow,
          token: getDisplayToken(borrow.chainId, borrow.borrowToken),
          value: borrow.borrowAmount,
          required: requiredWithBuffer,
          balance: undefined,
        })
        continue
      }

      if (requiredWithBuffer.lte(balance)) {
        spentByTokenAddress.set(borrow.borrowToken.address, requiredWithBuffer)
        runnableObjectives.push(createRepayObjective(borrow))
      } else {
        fundingIssues.push({
          borrow,
          token: getDisplayToken(borrow.chainId, borrow.borrowToken),
          value: borrow.borrowAmount,
          required: requiredWithBuffer,
          balance,
        })
      }
    }

    return { runnableObjectives, fundingIssues }
  }, [balancesByTokenAddress, borrows])

  return { runnableObjectives, fundingIssues, isLoading: enabled && (isLoading || isFetching || data === undefined) }
}

function SwapHelpPanel({
  fundingIssues,
  isCheckingBalances,
  startIndex,
  onOpenSwap,
}: {
  fundingIssues: RepayFundingIssue[]
  isCheckingBalances: boolean
  startIndex: number
  onOpenSwap: (defaultToToken?: Token) => void
}) {
  if (fundingIssues.length > 0 && !isCheckingBalances) {
    return (
      <div className="rounded-md border border-amber-500/40 bg-panel">
        <div className="grid grid-cols-[auto_auto_1fr_auto] gap-x-4 px-4 md:grid-cols-[auto_auto_auto_1fr_auto]">
          {fundingIssues.map((issue, index) => (
            <BlockedRepayRow
              key={`${issue.token.address}-${issue.required.toFixed()}`}
              issue={issue}
              index={startIndex + index}
              onOpenSwap={onOpenSwap}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="grid gap-4 rounded-md border border-border bg-panel p-4 sm:grid-cols-[1fr_auto] sm:items-center">
      <div>
        <Typography className="font-semibold text-sm">Need extra funds to repay?</Typography>
        <Typography variant="prompt" className="mt-2 text-sm leading-relaxed">
          {isCheckingBalances
            ? 'Checking wallet balances before preparing repay transactions.'
            : 'Swap from another wallet token if you need more funds for a repay transaction.'}
        </Typography>
      </div>
      <Button
        variant="outline"
        size="sm"
        prefixIcon={<ArrowUpDownIcon className="h-4 w-4" />}
        onClick={() => onOpenSwap()}
      >
        Open swap
      </Button>
    </div>
  )
}

function BlockedRepayRow({
  issue,
  index,
  onOpenSwap,
}: {
  issue: RepayFundingIssue
  index: number
  onOpenSwap: (defaultToToken?: Token) => void
}) {
  const formattedValue = `${issue.token.format(issue.value, { style: 'auto' })} ${issue.token.symbol}`
  const balanceText =
    issue.balance !== undefined
      ? `${issue.token.format(issue.balance, { style: 'auto' })} available, ${issue.token.format(issue.required, { style: 'auto' })} needed`
      : `Balance unavailable, ${issue.token.format(issue.required, { style: 'auto' })} needed`

  return (
    <div className="col-span-full grid min-h-[65px] grid-cols-subgrid items-center gap-y-2 border-border border-b py-4 last:border-none">
      <div className="text-muted-foreground text-xs tabular-nums">{index}.</div>
      <Image src={assets.actions.repay} alt="action-icon" className="h-4 w-4 shrink-0 opacity-60" />
      <div className="col-span-2 flex flex-col items-center justify-between gap-1.5 md:col-span-1 md:justify-start">
        <div>
          <p className="text-base text-foreground">Repay with {formattedValue}</p>
          <div className="text-muted-foreground text-sm">{balanceText}</div>
        </div>
      </div>

      <div className="col-span-full col-start-3 inline-flex items-center gap-4 md:col-span-1 md:mr-2 md:ml-auto">
        <div className="flex items-center gap-1">
          <Image src={assets.warning} alt="warning" className="h-4 w-4" />
          <strong className="ml-1 font-semibold text-amber-400 text-xs">Needs funds</strong>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="col-span-full ml-auto w-full md:col-span-1 md:col-start-[-1] md:w-auto"
          prefixIcon={<ArrowUpDownIcon className="h-4 w-4" />}
          onClick={() => onOpenSwap(issue.token)}
        >
          Open swap
        </Button>
      </div>
    </div>
  )
}

function WithdrawStep({
  deposits,
  completed,
  onComplete,
  onReset,
}: {
  deposits: Deposit[]
  completed: boolean
  onComplete: () => void
  onReset: () => void
}) {
  const [started, setStarted] = useState(false)
  const [startedObjectives, setStartedObjectives] = useState<Objective[] | undefined>()
  const [startedDeposits, setStartedDeposits] = useState<Deposit[] | undefined>()
  const objectives = useMemo<Objective[]>(() => deposits.map(createWithdrawObjective), [deposits])
  const {
    runnableObjectives,
    limitedWithdraws,
    isLoading: isLiquidityLoading,
  } = useWithdrawLiquidityPlan(startedDeposits ?? deposits)
  const actionObjectives = startedObjectives ?? (started ? runnableObjectives : objectives)
  const canRunWithdrawActions = !isLiquidityLoading && actionObjectives.length > 0

  if (!started && deposits.length === 0) {
    return <EmptyStep title="No isolated deposits" description="You can open a new position in active markets." />
  }

  if (completed) {
    return (
      <CompletedStep
        title="All withdraw transactions completed"
        description="Your isolated deposits have been withdrawn. You can move on to Euler deposits."
        onReset={() => {
          setStarted(false)
          setStartedObjectives(undefined)
          setStartedDeposits(undefined)
          onReset()
        }}
      />
    )
  }

  return (
    <div className="grid gap-4">
      <StepSummary
        title="Withdraw all isolated deposits"
        description="Max withdraw is selected for every isolated supply and collateral position. Where pool liquidity is short, the row shows the limit inline and you will confirm each withdraw transaction in your wallet."
        primaryLabel={started ? 'Withdraw flow started' : 'Start withdraw all'}
        disabled={started}
        onStart={() => {
          setStartedDeposits(deposits)
          setStarted(true)
        }}
      />
      {started && (
        <>
          {actionObjectives.length > 0 && (
            <div className="no-scrollbar max-h-[420px] overflow-y-auto">
              <DialogActionsPanel
                objectives={actionObjectives}
                enabled={canRunWithdrawActions}
                onFinish={() => {
                  setStartedObjectives(actionObjectives)
                  if (limitedWithdraws.length === 0) {
                    onComplete()
                  }
                }}
              />
            </div>
          )}
          <WithdrawLiquidityPanel limitedWithdraws={limitedWithdraws} isCheckingLiquidity={isLiquidityLoading} />
        </>
      )}
    </div>
  )
}

function createWithdrawObjective(deposit: Deposit): Objective {
  const isLending = deposit.type === 'lending'
  const cappedValue =
    isLending && deposit.poolAvailable !== undefined && deposit.poolAvailable.lt(deposit.lendAmount)
      ? deposit.poolAvailable
      : deposit.lendAmount

  return {
    type: deposit.type === 'lending' ? 'isolatedLendWithdraw' : 'isolatedWithdraw',
    token: getDisplayToken(deposit.chainId, deposit.collateralToken),
    value: cappedValue,
    ...(isLending
      ? {
          requestedValue: deposit.lendAmount,
          poolAvailable: deposit.poolAvailable,
        }
      : {}),
    pairAddress: deposit.pairAddress,
  }
}

interface WithdrawLiquidityIssue {
  token: Token
  value: NormalizedUnitNumber
  available: NormalizedUnitNumber
}

function useWithdrawLiquidityPlan(deposits: Deposit[]): {
  runnableObjectives: Objective[]
  limitedWithdraws: WithdrawLiquidityIssue[]
  isLoading: boolean
} {
  const { runnableObjectives, limitedWithdraws } = useMemo(() => {
    const runnableObjectives: Objective[] = []
    const limitedWithdraws: WithdrawLiquidityIssue[] = []

    for (const deposit of deposits) {
      if (deposit.type === 'lending' && deposit.poolAvailable !== undefined) {
        if (deposit.poolAvailable.lte(0)) {
          limitedWithdraws.push({
            token: getDisplayToken(deposit.chainId, deposit.collateralToken),
            value: deposit.lendAmount,
            available: deposit.poolAvailable,
          })
          continue
        }

        if (deposit.poolAvailable.lt(deposit.lendAmount)) {
          limitedWithdraws.push({
            token: getDisplayToken(deposit.chainId, deposit.collateralToken),
            value: deposit.lendAmount,
            available: deposit.poolAvailable,
          })
          runnableObjectives.push(createWithdrawObjective(deposit))
          continue
        }
      }

      runnableObjectives.push(createWithdrawObjective(deposit))
    }

    return { runnableObjectives, limitedWithdraws }
  }, [deposits])

  return { runnableObjectives, limitedWithdraws, isLoading: false }
}

function WithdrawLiquidityPanel({
  limitedWithdraws,
  isCheckingLiquidity,
}: {
  limitedWithdraws: WithdrawLiquidityIssue[]
  isCheckingLiquidity: boolean
}) {
  if (limitedWithdraws.length === 0) {
    return null
  }

  return (
    <div className="rounded-md border border-amber-500/40 bg-panel">
      {isCheckingLiquidity ? (
        <div className="p-4">
          <Typography className="font-semibold text-sm">Checking pool liquidity</Typography>
          <Typography variant="prompt" className="mt-2 text-sm leading-relaxed">
            Some withdrawals may still be blocked by pool liquidity.
          </Typography>
        </div>
      ) : (
        <div className="grid grid-cols-[auto_auto_1fr_auto] gap-x-4 p-4 md:grid-cols-[auto_auto_auto_1fr_auto]">
          {limitedWithdraws.map((issue, index) => (
            <BlockedWithdrawRow
              key={`${issue.token.address}-${issue.value.toFixed()}`}
              issue={issue}
              index={index + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function BlockedWithdrawRow({
  issue,
  index,
}: {
  issue: WithdrawLiquidityIssue
  index: number
}) {
  const formattedValue = `${issue.token.format(issue.value, { style: 'auto' })} ${issue.token.symbol}`

  return (
    <div className="col-span-full grid min-h-[65px] grid-cols-subgrid items-center gap-y-2 border-border border-b py-4 last:border-none">
      <div className="text-muted-foreground text-xs tabular-nums">{index}.</div>
      <Image src={assets.actions.withdraw} alt="action-icon" className="h-4 w-4 shrink-0 opacity-60" />
      <div className="col-span-2 flex flex-col items-center justify-between gap-1.5 md:col-span-1 md:justify-start">
        <div>
          <p className="text-base text-foreground">Withdraw {formattedValue}</p>
          <div className="text-muted-foreground text-sm">
            {issue.token.format(issue.available, { style: 'auto' })} available from the pool,{' '}
            {issue.token.format(issue.value, { style: 'auto' })} needed
          </div>
        </div>
      </div>
      <div className="col-span-full col-start-3 inline-flex items-center gap-1 md:col-span-1 md:mr-2 md:ml-auto">
        <Image src={assets.warning} alt="warning" className="h-4 w-4" />
        <strong className="ml-1 font-semibold text-amber-400 text-xs">Pool liquidity limited</strong>
      </div>
      <div className="col-span-full col-start-[-1] md:col-span-1 md:ml-auto" />
    </div>
  )
}

function OpenNewPositionStep() {
  return (
    <div className="grid gap-4 rounded-md border border-border bg-panel p-4 sm:grid-cols-[1fr_auto] sm:items-center">
      <div>
        <Typography className="font-semibold text-sm">Open a new position</Typography>
        <Typography variant="prompt" className="mt-2 text-sm leading-relaxed">
          Use Quick Borrow to open a new position in active markets once your legacy position is closed.
        </Typography>
      </div>
      <LinkButton href={paths.borrow} variant="primary" size="sm">
        Open new position
      </LinkButton>
    </div>
  )
}

function EmptyStep({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-md border border-border bg-panel p-4">
      <Typography className="font-semibold text-sm">{title}</Typography>
      <Typography variant="prompt" className="mt-2 text-sm leading-relaxed">
        {description}
      </Typography>
    </div>
  )
}

function CompletedStep({
  title,
  description,
  onReset,
}: {
  title: string
  description: string
  onReset: () => void
}) {
  return (
    <div className="grid gap-4 rounded-md border border-primary/30 bg-panel p-4">
      <div>
        <Typography className="font-semibold text-sm">{title}</Typography>
        <Typography variant="prompt" className="mt-2 text-sm leading-relaxed">
          {description}
        </Typography>
      </div>
      <Button variant="outline" size="sm" className="justify-self-end" onClick={onReset}>
        View actions
      </Button>
    </div>
  )
}

function StepSummary({
  title,
  description,
  primaryLabel,
  disabled,
  onStart,
}: {
  title: string
  description: string
  primaryLabel: string
  disabled?: boolean
  onStart: () => void
}) {
  return (
    <div className="grid gap-4 rounded-md border border-border bg-panel p-4 sm:grid-cols-[1fr_auto] sm:items-center">
      <div>
        <Typography className="font-semibold text-sm">{title}</Typography>
        <Typography variant="prompt" className="mt-2 text-sm leading-relaxed">
          {description}
        </Typography>
      </div>
      <div className="flex flex-wrap gap-2 sm:justify-end">
        <Button variant="primary" size="sm" disabled={disabled} onClick={onStart}>
          {primaryLabel}
        </Button>
      </div>
    </div>
  )
}

function getStepCount(step: MigrationStep, deposits: Deposit[], borrows: Borrow[]) {
  switch (step) {
    case 'repay':
      return borrows.length
    case 'withdraw':
      return deposits.length
    case 'openEuler':
      return 1
  }
}
